/**
 * PARA progress-table LMS slideout: resolves next challenge when API shape varies
 * (`questionId`, label match, `highestChallenge` + questions list).
 * LMS story assets use 1-based challenge numbers; prefer `questionNumber` when available.
 */

export function questionIdFromChallenge(challenge: any): number | null {
	if (challenge == null || typeof challenge !== 'object') {
		return null;
	}
	const raw = challenge.id ?? challenge.questionId ?? challenge.challengeId ?? challenge.question?.id;
	if (raw == null) {
		return null;
	}
	const n = Number(raw);
	return Number.isNaN(n) ? null : n;
}

function sortQuestionsById(questions: any[]): any[] {
	return [...(questions || [])].sort((a, b) => Number(a.id) - Number(b.id));
}

/**
 * Ordered question catalog for the student's current phase (book + template), matching LMS traversal.
 */
export function getPhaseQuestionCatalog(
	questions: any[],
	studentAssessmentBookId?: string | number | null,
	studentAssessmentTemplateId?: string | number | null,
	current?: any
): any[] {
	const qs = Array.isArray(questions) ? questions : [];
	if (qs.length === 0) {
		return [];
	}
	const bookId = current?.assessmentBookId ?? studentAssessmentBookId;
	const templateId = current?.assessmentTemplateId ?? studentAssessmentTemplateId;
	const pickOrdered = (list: any[]): any[] => sortQuestionsById(list);

	let phaseQs = pickOrdered(qs);
	if (bookId != null && bookId !== '') {
		phaseQs = pickOrdered(phaseQs.filter((q) => String(q.assessmentBookId) === String(bookId)));
	}
	if (templateId != null && templateId !== '' && phaseQs.length > 0) {
		const byTemplate = pickOrdered(phaseQs.filter((q) => String(q.assessmentTemplateId) === String(templateId)));
		if (byTemplate.length > 0) {
			phaseQs = byTemplate;
		}
	}
	if (phaseQs.length === 0) {
		phaseQs = pickOrdered(qs);
	}
	return phaseQs;
}

/** 1-based position of the current challenge within the phase catalog (e.g. 2 of 15). */
export function getChallengePositionInCatalog(
	current: any,
	questions: any[],
	student?: any
): { position: number; total: number } | null {
	if (!current || typeof current !== 'object') {
		return null;
	}
	const phaseQs = getPhaseQuestionCatalog(
		questions,
		student?.assessmentBookId,
		student?.assessmentTemplateId,
		current
	);
	if (phaseQs.length === 0) {
		return null;
	}
	const currentId = questionIdFromChallenge(current);
	let idx = currentId != null ? phaseQs.findIndex((q) => Number(q.id) === currentId) : -1;
	if (idx < 0) {
		const qn = Number((current as any).questionNumber);
		if (Number.isFinite(qn) && qn > 0) {
			idx = phaseQs.findIndex((q) => Number(q.questionNumber) === Math.floor(qn));
			if (idx < 0 && qn >= 1 && qn <= phaseQs.length) {
				return { position: Math.floor(qn), total: phaseQs.length };
			}
		}
	}
	if (idx < 0) {
		return null;
	}
	return { position: idx + 1, total: phaseQs.length };
}

/**
 * Next challenge in the student's phase (assessmentBookId), by catalog order — not `currentId + 1`
 * (IDs can have gaps; `questions` array order is not guaranteed).
 */
export function getNextChallengeQuestionAfter(
	current: any,
	questions: any[],
	studentAssessmentBookId?: string | number | null,
	studentAssessmentTemplateId?: string | number | null
): any | null {
	if (!current || typeof current !== 'object') {
		return null;
	}
	const qs = Array.isArray(questions) ? questions : [];
	if (qs.length === 0) {
		return null;
	}
	const currentId = questionIdFromChallenge(current);
	if (currentId == null) {
		return null;
	}
	const phaseQs = getPhaseQuestionCatalog(questions, studentAssessmentBookId, studentAssessmentTemplateId, current);

	const idx = phaseQs.findIndex((q) => Number(q.id) === currentId);
	if (idx >= 0 && idx < phaseQs.length - 1) {
		return normalizeNextChallengeForLms(phaseQs[idx + 1], qs);
	}
	// At last question in phase (idx === length - 1): do not fall back to id+1 — another phase
	// in the same book often uses the next integer id and would be wrongly returned.
	if (idx === -1) {
		const sequentialIdNext = qs.find((qq) => Number(qq.id) === currentId + 1);
		if (sequentialIdNext) {
			return normalizeNextChallengeForLms(sequentialIdNext, qs);
		}
	}
	return null;
}

/**
 * Previous challenge in the student's phase (assessmentBookId), by catalog order — not `currentId - 1`.
 */
export function getPreviousChallengeQuestionBefore(
	current: any,
	questions: any[],
	studentAssessmentBookId?: string | number | null,
	studentAssessmentTemplateId?: string | number | null
): any | null {
	if (!current || typeof current !== 'object') {
		return null;
	}
	const qs = Array.isArray(questions) ? questions : [];
	if (qs.length === 0) {
		return null;
	}
	const currentId = questionIdFromChallenge(current);
	if (currentId == null) {
		return null;
	}
	const phaseQs = getPhaseQuestionCatalog(questions, studentAssessmentBookId, studentAssessmentTemplateId, current);

	const idx = phaseQs.findIndex((q) => Number(q.id) === currentId);
	if (idx > 0) {
		return normalizeNextChallengeForLms(phaseQs[idx - 1], qs);
	}
	return null;
}

/**
 * Merge API `nextChallenge` with the questions catalog so `question.id` exists for
 * score storage keys (ScoreStorageService keys by question.id; missing id overwrites).
 */
export function normalizeNextChallengeForLms(nextChallenge: any, questions: any[]): any | null | undefined {
	if (nextChallenge == null || typeof nextChallenge !== 'object') {
		return nextChallenge;
	}
	const id = questionIdFromChallenge(nextChallenge);
	if (id == null) {
		return nextChallenge;
	}
	const qs = Array.isArray(questions) ? questions : [];
	const full = qs.find((q) => Number(q.id) === id);
	if (full) {
		return { ...full, ...nextChallenge, id: full.id };
	}
	return { ...nextChallenge, id };
}

export function lmsStoryUrlFromNextChallenge(nextChallenge: any, assessmentId?: string | null): string | null {
	if (nextChallenge == null || typeof nextChallenge !== 'object') {
		return null;
	}
	const rawFolder = nextChallenge.questionNumber != null ? nextChallenge.questionNumber : questionIdFromChallenge(nextChallenge);
	if (rawFolder == null) {
		return null;
	}
	const folder = Number(rawFolder);
	if (!Number.isFinite(folder) || folder <= 0) {
		return null;
	}
	const base = (assessmentId || '').trim() ? encodeURIComponent(String(assessmentId).trim()) : 'para-ce';
	// Asset folders are `src/assets/LMS/para-ce/Challenge <n>/story.html` (space in folder name).
	return `/assets/LMS/${base}/Challenge%20${folder}/story.html`;
}

/** Progress-table rows use `challange` (API spelling) or rarely `challenge`. */
function challangeBlock(student: any): any | null {
	return student?.challange ?? student?.challenge ?? null;
}

/**
 * Copy `highestChallenge` from the progress report row onto the student from `getStudents$`
 * when the API omits `challange` (common on PARA progress data).
 */
export function mergeProgressRowChallangeHints(apiStudent: any, partialFromProgressRow: any): void {
	if (!apiStudent || partialFromProgressRow?.highestChallenge == null) {
		return;
	}
	const hi = partialFromProgressRow.highestChallenge;
	const block = challangeBlock(apiStudent);
	if (!block) {
		apiStudent.challange = { highestChallenge: hi };
		return;
	}
	if (block.highestChallenge == null) {
		block.highestChallenge = hi;
	}
}

export function getEffectiveNextChallenge(student: any, questions: any[]): any | null {
	const chall = challangeBlock(student);
	if (!chall) {
		return null;
	}

	if (chall.nextChallenge === 'completed') {
		return null;
	}

	const qs = Array.isArray(questions) ? questions : [];
	const nc = chall.nextChallenge;

	if (nc && typeof nc === 'object') {
		const id = questionIdFromChallenge(nc);
		if (id != null) {
			return { ...nc, id };
		}
		const label = nc.label ?? nc.question;
		if (label && qs.length > 0) {
			const byLabel = qs.find((q) => {
				if (q.label != null && String(q.label).trim() === String(label).trim()) {
					return true;
				}
				if (q.question != null && String(q.question).trim() === String(label).trim()) {
					return true;
				}
				return false;
			});
			if (byLabel) {
				return byLabel;
			}
		}
	}

	const hi = questionIdFromChallenge(chall.highestChallenge);
	if (hi != null && qs.length > 0) {
		const nextQ = qs.find((q) => Number(q.id) === Number(hi) + 1);
		if (nextQ) {
			return nextQ;
		}
	}

	if (nc && typeof nc === 'object') {
		return nc;
	}
	return null;
}
